import { GitBranch } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { GithubMark } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/overlay";
import { Field, Input } from "@/components/ui/primitives";
import { errorMessage, post } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { isRemoteUrl, remoteName, repoLabel, resolveRepoInput } from "@/workspace/git";
import { desktop } from "./bridge";
import { useDesktopUi } from "./ui";
import { openGitHubConnect, useGitHubAccount } from "./github-connect";

/**
 * Arquivo › Importar repositório Git: GitHub (pela conta conectada) ou qualquer servidor por HTTPS
 * (GitLab, Bitbucket, Azure DevOps, Gitea…). Cria um projeto vazio e abre-o com o pedido de
 * importação; o projeto aberto traz os arquivos pela extensão Git (sem o limite de criação).
 */
export function ImportRepoDialog() {
  const open = useDesktopUi((s) => s.importRepo);
  const set = useDesktopUi((s) => s.set);
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [repo, setRepo] = useState("");
  const [branch, setBranch] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [needsLogin, setNeedsLogin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const account = useGitHubAccount(open);
  const githubConnected = Boolean(account.data?.connected);
  const looksRemote = /:\/\/|@/.test(repo) && !/github\.com/i.test(repo);

  const close = () => {
    set({ importRepo: false });
    setError(null);
    setNeedsLogin(false);
  };

  /** Branch padrão (e se o servidor pede login), pela API do GitHub ou pelo Git por HTTPS. */
  const lookup = async (full: string): Promise<{ name: string; branch: string }> => {
    if (!isRemoteUrl(full)) {
      const info = await desktop!.git.api<{ default_branch?: string; name?: string; message?: string }>(
        "GET",
        `/repos/${full}`,
      );
      if (info.status === 404) throw new Error("Repositório não encontrado (ou sem acesso com o seu token).");
      if (info.status >= 400) throw new Error(info.data.message ?? `O GitHub respondeu ${info.status}.`);
      return { name: info.data.name ?? full.split("/")[1]!, branch: info.data.default_branch || "main" };
    }
    if (username.trim() && password.trim()) {
      const saved = await desktop!.gitRemote.credentials.set(full, username, password);
      if (!saved.ok) throw new Error(saved.error ?? "Não foi possível salvar a credencial.");
      setPassword("");
    }
    const info = await desktop!.gitRemote.info(full);
    if (!info.ok) {
      if (info.status === 401) setNeedsLogin(true);
      throw new Error(info.error);
    }
    const name = new URL(full).pathname
      .split("/")
      .filter(Boolean)
      .pop()!
      .replace(/\.git$/, "");
    return { name, branch: info.data.defaultBranch ?? info.data.branches[0] ?? "main" };
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const full = await resolveRepoInput(repo, githubConnected);
      if (!full) throw new Error("Use dono/nome do GitHub ou o endereço https do repositório.");
      if (isRemoteUrl(full) && !desktop?.gitRemote)
        throw new Error("Atualize o Kompil Desktop para importar de outros servidores.");
      const found = await lookup(full);
      const b = branch.trim() || found.branch;
      const created = await post<{ project: { id: string } }>("/projects", {
        name: found.name.slice(0, 80),
        description: `Importado de ${isRemoteUrl(full) ? repoLabel(full) : `github.com/${full}`}`,
        template: "blank",
        visibility: "private",
      });
      close();
      setRepo("");
      setBranch("");
      navigate(
        `/p/${created.project.id}?importar=${encodeURIComponent(full)}&branch=${encodeURIComponent(b)}`,
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!desktop) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? set({ importRepo: true }) : close())}>
      <DialogContent
        title="Importar repositório Git"
        description="GitHub, GitLab, Bitbucket, Azure DevOps, Gitea ou qualquer servidor por HTTPS. Cria um projeto com os arquivos da branch, já ligado ao repositório (commit, push e pull)."
      >
        {me?.user.isGuest ? (
          <div className="space-y-3 text-[13px] text-fg-muted">
            <p>O Git é ligado à sua conta do Kompil. Entre (é grátis) para importar repositórios.</p>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => set({ importRepo: false, login: "Entre para importar repositórios." })}
              >
                Entrar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field
              label="Repositório"
              hint={
                githubConnected
                  ? `dono/nome do GitHub (você: ${account.data?.name ? `${account.data.name}, ` : ""}@${account.data?.login}) ou o endereço https de qualquer servidor`
                  : "Endereço https (ou git@servidor:caminho) de qualquer servidor Git"
              }
              error={error}
            >
              <Input
                value={repo}
                onChange={(e) => {
                  setRepo(e.target.value);
                  setNeedsLogin(false);
                }}
                placeholder="https://gitlab.com/voce/meu-site.git"
                spellCheck={false}
                autoFocus
              />
            </Field>
            <Field label="Branch" hint="Em branco: a branch padrão do repositório">
              <Input
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="main"
                spellCheck={false}
              />
            </Field>
            {looksRemote || needsLogin || (!githubConnected && repo.trim()) ? (
              <Field
                label={needsLogin ? "Este repositório pede login" : "Credencial (repositório privado)"}
                hint="Usuário e token (ou senha de app). Ficam no cofre do sistema, só para este servidor."
              >
                <div className="grid grid-cols-2 gap-1.5">
                  <Input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={/gitlab/i.test(repo) ? "oauth2" : "usuário"}
                    aria-label="Usuário"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="token"
                    aria-label="Token ou senha de app"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
              </Field>
            ) : null}
            {!githubConnected && account.data ? (
              <p className="text-[12px] text-fg-subtle">
                Repositório privado do GitHub?{" "}
                <button
                  type="button"
                  className="text-fg-muted underline underline-offset-4 hover:text-fg"
                  onClick={openGitHubConnect}
                >
                  Conecte sua conta do GitHub
                </button>
                .
              </p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" loading={busy} disabled={!repo.trim()}>
                {looksRemote ? <GitBranch className="h-3.5 w-3.5" /> : <GithubMark className="h-3.5 w-3.5" />}{" "}
                Importar{looksRemote && repo.trim() ? ` de ${remoteHint(repo)}` : ""}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** "gitlab.com" do que a pessoa digitou (só para o botão). */
function remoteHint(input: string) {
  const scp = input.trim().match(/^[\w.-]+@([\w.-]+):/);
  if (scp) return scp[1]!;
  try {
    return remoteName(new URL(input.trim()).href);
  } catch {
    return "outro servidor";
  }
}
